/**
 * Semantic Versioning (SemVer) utility for legal documents.
 * Format: MAJOR.MINOR.PATCH (e.g., "1.0.0", "1.2.3")
 */

export const SEMVER_REGEX = /^(\d+)\.(\d+)\.(\d+)$/;

export type VersionChangeType = "MAJOR" | "MINOR" | "PATCH";

export interface ChangeTypeOption {
  value: VersionChangeType;
  label: string;
  description: string;
  example: string;
}

export const CHANGE_TYPE_OPTIONS: ChangeTypeOption[] = [
  {
    value: "MAJOR",
    label: "Major",
    description:
      "Significant changes to legal rights, responsibilities, obligations, or conditions.",
    example: "Increments MAJOR, resets MINOR and PATCH to zero (e.g. 1.2.3 → 2.0.0)",
  },
  {
    value: "MINOR",
    label: "Minor",
    description:
      "Meaningful additions, new sections, or clarifications that do not fundamentally change the legal framework.",
    example: "Increments MINOR, resets PATCH to zero (e.g. 1.2.3 → 1.3.0)",
  },
  {
    value: "PATCH",
    label: "Patch",
    description:
      "Typographical, formatting, or other corrections that do not change the legal meaning.",
    example: "Increments PATCH number (e.g. 1.2.3 → 1.2.4)",
  },
];

/**
 * Check if a string is a valid SemVer format.
 */
export function isValidSemVer(version: unknown): boolean {
  if (typeof version !== "string") return false;
  return SEMVER_REGEX.test(version.trim());
}

/**
 * Parse a SemVer string into its numeric components.
 */
export function parseSemVer(
  version: unknown
): { major: number; minor: number; patch: number } | null {
  if (version === null || version === undefined) return null;
  const str = String(version).trim();
  const match = str.match(SEMVER_REGEX);
  if (!match) return null;

  return {
    major: parseInt(match[1], 10),
    minor: parseInt(match[2], 10),
    patch: parseInt(match[3], 10),
  };
}

/**
 * Format numeric components into MAJOR.MINOR.PATCH.
 */
export function formatSemVer(
  major: number | string,
  minor: number | string,
  patch: number | string
): string {
  return `${Math.max(0, parseInt(String(major), 10) || 0)}.${Math.max(
    0,
    parseInt(String(minor), 10) || 0
  )}.${Math.max(0, parseInt(String(patch), 10) || 0)}`;
}

/**
 * Normalize an arbitrary version to standard "MAJOR.MINOR.PATCH".
 * E.g., integer 1 -> "1.0.0", "v1.2.3" -> "1.2.3".
 */
export function normalizeVersion(version: unknown): string {
  if (version === null || version === undefined) {
    return "1.0.0";
  }

  let str = String(version).trim();
  if (str.startsWith("v") || str.startsWith("V")) {
    str = str.slice(1);
  }

  if (SEMVER_REGEX.test(str)) {
    return str;
  }

  if (/^\d+$/.test(str)) {
    return `${str}.0.0`;
  }

  if (/^\d+\.\d+$/.test(str)) {
    return `${str}.0`;
  }

  return str;
}

/**
 * Format a version for clean user-facing display in format MAJOR.MINOR.PATCH.
 */
export function formatVersionDisplay(version: unknown, withPrefix = false): string {
  const norm = normalizeVersion(version);
  return withPrefix ? `v${norm}` : norm;
}

/**
 * Compare two SemVer strings.
 * Returns -1 if v1 < v2, 0 if equal, 1 if v1 > v2.
 */
export function compareSemVer(v1: unknown, v2: unknown): number {
  const p1 = parseSemVer(normalizeVersion(v1));
  const p2 = parseSemVer(normalizeVersion(v2));

  if (!p1 && !p2) return 0;
  if (!p1) return -1;
  if (!p2) return 1;

  if (p1.major !== p2.major) {
    return p1.major > p2.major ? 1 : -1;
  }
  if (p1.minor !== p2.minor) {
    return p1.minor > p2.minor ? 1 : -1;
  }
  if (p1.patch !== p2.patch) {
    return p1.patch > p2.patch ? 1 : -1;
  }
  return 0;
}

/**
 * Calculate the next SemVer version based on current version and change type.
 *
 * Rules:
 * - Initial (no current version): "1.0.0"
 * - MAJOR: increment major, reset minor and patch to 0 (1.2.3 -> 2.0.0)
 * - MINOR: increment minor, reset patch to 0 (1.2.3 -> 1.3.0)
 * - PATCH: increment patch (1.2.3 -> 1.2.4)
 */
export function getNextSemVer(
  currentVersion: unknown,
  changeType: VersionChangeType = "MINOR"
): string {
  if (!currentVersion) {
    return "1.0.0";
  }

  const normalized = normalizeVersion(currentVersion);
  const parsed = parseSemVer(normalized);

  if (!parsed) {
    return "1.0.0";
  }

  const normalizedType = String(changeType || "MINOR").toUpperCase().trim() as VersionChangeType;

  switch (normalizedType) {
    case "MAJOR":
      return formatSemVer(parsed.major + 1, 0, 0);
    case "MINOR":
      return formatSemVer(parsed.major, parsed.minor + 1, 0);
    case "PATCH":
      return formatSemVer(parsed.major, parsed.minor, parsed.patch + 1);
    default:
      return formatSemVer(parsed.major, parsed.minor + 1, 0);
  }
}
